import { defineStore } from "pinia";
import { useCartStore } from "../cartstore/cartStore";
import { useKidsCartStore } from "../cartstore/kidsCartStore";

const TOKEN_EXPIRATION_MINUTES = 30; // same as backend token expiration

const LS = {
  TOKEN: "access_token",
  USER: "user_data",
  RECEIVED_AT: "token_received_at",
};

function parseStoredUser() {
  try {
    const raw = localStorage.getItem(LS.USER);
    if (raw == null || raw === "") return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function parseTokenReceivedAt() {
  const n = parseInt(localStorage.getItem(LS.RECEIVED_AT) || "0", 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    accessToken: localStorage.getItem(LS.TOKEN) || null,
    user: parseStoredUser(),
    tokenReceivedAt: parseTokenReceivedAt(),
  }),

  getters: {
    isAuthenticated(state) {
      if (!state.accessToken || typeof state.accessToken !== "string") return false;
      const receivedAt = state.tokenReceivedAt || 0;
      if (receivedAt === 0) return false;
      const now = Date.now();
      const expiryTime = receivedAt + TOKEN_EXPIRATION_MINUTES * 60 * 1000;
      return now < expiryTime;
    },
  },

  actions: {
    setAuthData(token, user = null) {
      if (!token || typeof token !== "string") return;
      const trimmed = token.trim();
      if (!trimmed) return;

      this.accessToken = trimmed;
      this.tokenReceivedAt = Date.now();
      localStorage.setItem(LS.TOKEN, trimmed);
      localStorage.setItem(LS.RECEIVED_AT, String(this.tokenReceivedAt));

      if (user && typeof user === "object") {
        this.user = user;
        localStorage.setItem(LS.USER, JSON.stringify(user));
      }
      const cart = useCartStore();
      cart.handleLoginSession();
      const kidsCart = useKidsCartStore();
      kidsCart.handleLoginSession();
    },

    checkToken() {
      const fromStorage = localStorage.getItem(LS.TOKEN);
      const raw = this.accessToken || fromStorage;
      const token = typeof raw === "string" ? raw.trim() : "";

      if (!token) {
        // Guest browsing: do not run logout (avoids cart.logout on every route).
        if (this.accessToken != null || (fromStorage != null && fromStorage !== "")) {
          this.logout();
        }
        return false;
      }

      if (!this.accessToken && fromStorage) {
        this.accessToken = token;
        this.tokenReceivedAt = parseTokenReceivedAt();
        const u = parseStoredUser();
        if (u) this.user = u;
      }

      const receivedAt = this.tokenReceivedAt || parseTokenReceivedAt();
      if (receivedAt === 0) {
        this.logout();
        return false;
      }

      const now = Date.now();
      const expiryTime = receivedAt + TOKEN_EXPIRATION_MINUTES * 60 * 1000;
      if (now >= expiryTime) {
        this.logout();
        return false;
      }

      return true;
    },

    logout() {
      this.accessToken = null;
      this.user = null;
      this.tokenReceivedAt = 0;
      const cart = useCartStore();
      cart.logout();
      const kidsCart = useKidsCartStore();
      kidsCart.logout();
      localStorage.removeItem(LS.TOKEN);
      localStorage.removeItem(LS.USER);
      localStorage.removeItem(LS.RECEIVED_AT);
    },

    updateUser(data) {
      if (!data || typeof data !== "object") return;
      this.user = { ...(this.user || {}), ...data };
      localStorage.setItem(LS.USER, JSON.stringify(this.user));
    },
  },
});
